// TODO: Opportunity to create a hook that provides the shared state (timer, stage, graphemeIndex, etc) and shared methods
import React, { useState, useEffect, useMemo, Fragment, useRef } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import ListeningStatus from "Components/Global/ListeningStatus";
import { Container, Image, Box } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import { listen, abortListen } from "Services/Listen";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";
import useBlendingTaskPlacementListening from "../shared/hooks/useBlendingTaskPlacementListening";
import "./ElkoninBox.scss";
import icon from "./elkonin-icon.png";
import { getDemoWords } from "./demo";
import classNames from "classnames";
import GraphemeText from "./GraphemeText";
import { getUserData } from "Services/API";
import { ipaToAmirabet } from "../../../app/services/API";
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

const MAX_LISTEN_TIME = 5000;
let maxListenTimeout = null;

const validVersionIds = [1, 2, 3, 4, 99];

const isValidVersionId = (id) => validVersionIds.includes(id);

export default function ElkoninBoxValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.elkonin_box;

  const { id } = useVersion(props.wordMetadata, {
    //Intervention versions
    //0: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    1: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    2: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    3: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    4: ["PHON", "GRAPHEMIC_BREAKDOWN"],

    // assessment/tutor activity version. Don't allow randomly choosing this as intervention doesn't support it
    // 99: ["PHON", "GRAPHEMIC_BREAKDOWN"],
  });

  // Specify the array to account for 0.  
  const versionId = isValidVersionId(props.version) ? props.version : id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!isValidVersionId(versionId)) close();
  }, [versionId]);

  if (!isValidVersionId(versionId)) return null;

  const Version = {
    //Intervention versions
    0: ElkoninBox0,
    1: ElkoninBox1,
    2: ElkoninBox2,
    3: ElkoninBox3,
    4: ElkoninBox4,

    // assessment/tutor activity version
    99: ElkoninBox99,
  }[versionId];

  return <Version {...props} close={close} stopListen={stopListen} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    // We dont have this in Elkonin99
    if (props.stopFakeListen) {
      props.stopFakeListen();
    }
    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }

  function stopListen() {
    abortListen(props.speech, props.endOfPhrase);
  }
}

function ElkoninBox0(props) {
  const { locale } = getUserData();
  const { word, wordMetadata, speech, tutorMode, startFakeListen, stopFakeListen } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");

  const [stage, setStage] = useState(0);
  const [graphemeIndex, setGraphemeIndex] = useState(0);
  const [showGraphemes, setShowGraphemes] = useState(false);

  const { speak, speakFile, listenWord } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const [timer, setTimer] = useState(null);

  useEffect(() => {
    return () => clearTimeout(timer);
  }, [timer]);

  useEffect(() => {
    if (stage !== 1) return;
    if (graphemeIndex === graphemes.length) return;
    const job = setTimeout(onClickBall, 7500);
    setTimer(job);
  }, [stage, graphemeIndex]);

  const files = getAudioForPhonemes(PHON, locale);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0: {
          const nSounds = files.length;
          await cancellablePromise(speak(ttsStrings.intro_1({ nSounds })));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        }
        case 1:
          startFakeListen();
          break;
        case 2:
          await cancellablePromise(sleep(500));
          setShowGraphemes(true);
          await cancellablePromise(sleep(1500));
          await cancellablePromise(speak(ttsStrings.ask_word));
          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      {!tutorMode && <ListeningStatus />}
    </Container>
  );

  function renderContent() {
    return (
      <div className="elkonin-boxes">
        {graphemes.map((grapheme, i) => (
          <BoxWithBall
            key={i}
            filled={i < graphemeIndex}
            grapheme={grapheme}
            showGrapheme={showGraphemes}
            disabled={stage !== 1}
            onClickBall={() => {
              if (i !== graphemeIndex) return;
              onClickBall();
            }}
          />
        ))}
      </div>
    );
  }

  function onClickBall() {
    const i = graphemeIndex;
    setGraphemeIndex(i + 1);
    speakFile(files[i]);

    // all boxes have been filled
    if (i === graphemes.length - 1) {
      setTimer(null);
      stopFakeListen();
      setStage(2);
    }
  }
}

function ElkoninBox1(props) {
  const { locale } = getUserData();
  const { word, wordMetadata, speech, ftue, startFakeListen, stopFakeListen } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [graphemeIndex, setGraphemeIndex] = useState(0);

  const { speak, speakSequence, speakSlowly, speakPhonemes, listenWord } =
    useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, locale);
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    return () => clearTimeout(timer);
  }, [timer]);

  useEffect(() => {
    if (stage !== 1) return;
    if (graphemeIndex === graphemes.length) { 
      stopFakeListen();
      return;
    }
    const job = setTimeout(onClickBall, 7500);
    setTimer(job);
  }, [stage, graphemeIndex]);


  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro_2));
          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({
            phon: PHON,
            delay: 750,
            onSpeak: (i) => setGraphemeIndex(i + 1),
          }));
          await cancellablePromise(speak(ttsStrings.blend));
          await cancellablePromise(speakSlowly({textToSpeak: word, skipTranslation: getUserData().bilingualMode}));
          await cancellablePromise(sleep());
          setGraphemeIndex(0);
          await cancellablePromise(speak(ttsStrings.your_turn));
          await cancellablePromise(speak(ttsStrings.lets_work_on({word})))
          await cancellablePromise(speak(ttsStrings.ask_sounds_1({word})));
          setStage(1);
          break;
        case 1:
          if (!ftue) {
            await cancellablePromise(speak(ttsStrings.lets_work_on({word})))
            await cancellablePromise(speak(ttsStrings.ask_sounds_1({word})));
          }
          startFakeListen();
          break;
        case 2: {
          await cancellablePromise(sleep(2000));
          await cancellablePromise(speak(ttsStrings.ask_blend));
          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          if (status === "match") {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.correct,
                {textToSpeak: word, skipTranslation: true},
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.correct} ${word}`));
          }
          else {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.struggle_part_1,
                {textToSpeak: word, skipTranslation: true},
                ttsStrings.struggle_part_2,
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.struggle_part_1} ${word}. ${ttsStrings.struggle_part_2}`));
          } // struggle or silence
          props.close();
          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      <ListeningStatus />
    </Container>
  );

  function renderContent() {
    return (
      <div className="elkonin-boxes r-3">
        {graphemes.map((grapheme, i) => {
          const filled = i < graphemeIndex;

          let showGrapheme = false;
          if (stage === 1 && filled) showGrapheme = true;
          if (stage > 1) showGrapheme = true;

          return (
            <BoxWithBall
              key={i}
              filled={filled}
              grapheme={grapheme}
              showGrapheme={showGrapheme}
              disabled={stage !== 1}
              graphemePosition="above"
              onClickBall={() => {
                if (i !== graphemeIndex) return;
                onClickBall();
              }}
            />
          );
        })}
      </div>
    );
  }

  function onClickBall() {
    const i = graphemeIndex;
    setGraphemeIndex(i + 1);

    // all boxes have been filled
    if (i >= graphemes.length - 1) {
      stopFakeListen();
      setTimer(null);
      setStage(2);
    }
  }
}

function ElkoninBox2(props) {
  const { locale } = getUserData();
  const demo = getDemoWords(locale);
  const { word, wordMetadata, speech, ftue, startFakeListen, stopFakeListen } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN, IMG_URL } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [graphemeIndex, setGraphemeIndex] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const distractors = useMemo(() => {
    const n = Math.floor(Math.random() * 3);
    const arr = [];
    for (let i = 0; i < n; i++) {
      // don't exceed 5 boxes
      if (arr.length + graphemes.length > 5) continue;
      arr.push("");
    }
    return arr;
  }, []);

  const { speak, speakSequence, speakPhonemes, listenWord } = useConversation(
    speech,
    listen
  );
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, locale);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro_2));
          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({
            phon: demo.dog.PHON,
            delay: 750,
            onSpeak: (i) => setGraphemeIndex(i + 1),
          }));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speak(ttsStrings.ignore_extras));
          setStage(1);
          break;
        case 1:
          setGraphemeIndex(0);
          if (ftue) await cancellablePromise(speak(ttsStrings.your_turn));
          await cancellablePromise(speak(ttsStrings.ask_sounds_2));
          setStage(2);
          break;
        case 2:
          startFakeListen();
          break;
        case 3: {
          await cancellablePromise(sleep(2000));
          await cancellablePromise(speak(ttsStrings.ask_blend));
          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          if (status === "match") {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.correct,
                {textToSpeak: word, skipTranslation: true},
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.correct} ${word}`));
          }
          else {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.struggle_part_1,
                {textToSpeak: word, skipTranslation: true},
                ttsStrings.struggle_part_2,
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.struggle_part_1} ${word}. ${ttsStrings.struggle_part_2}`));
          }
          props.close();
          break;
        }
        case 4: {
          // feedback level 1
          // reset if too many balls
          if (graphemeIndex >= graphemes.length) {
            setGraphemeIndex(0);
            await cancellablePromise(speak(ttsStrings.too_many_sounds));
          }
          const nSounds = files.length;

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.describe_1,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.describe_2({ nSounds })
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.describe_1} ${word} ${ttsStrings.describe_2({ nSounds })}`));

          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({ phon: PHON, delay: 500 }));

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.feedback_1,
              {textToSpeak: word, skipTranslation: true},
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.feedback_1} ${word}`));

          setAttempts(1);
          setStage(2);
          break;
        }
        case 5:
          // feedback level 2
          setGraphemeIndex(0);

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.feedback_2_part_1,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.feedback_2_part_2,
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.feedback_2_part_1} ${word} ${ttsStrings.feedback_2_part_2}`));

          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({
            phon: PHON,
            delay: 750,
            onSpeak: (i) => setGraphemeIndex(i + 1),
          }));
          setStage(3);
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  // evaluate correctness on graphemeIndex change
  // apply a timeout to allow "too many balls" or "not enough balls" feedback
  useEffect(() => {
    if (stage !== 2) return;

    let timeout = 4500;

    // reduce timeout when correct
    const isCorrect = graphemeIndex === graphemes.length;
    if (isCorrect) timeout = 2500;

    // reduce timeout when maxed out on balls
    if (graphemeIndex === graphemes.length + distractors.length) timeout = 2500;

    const job = setTimeout(() => {
      stopFakeListen();
      if (isCorrect) setStage(3);
      else {
        if (attempts === 0) setStage(4);
        if (attempts === 1) setStage(5);
      }
    }, timeout);
    return () => clearTimeout(job);
  }, [graphemeIndex, stage]);

  return (
    <Container icon={icon}>
      {renderContent()}
      <ListeningStatus />
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      return (
        <>
          <ElkoninImage src={demo.dog.IMG_URL} />
          <div className="elkonin-boxes">
            {demo.dog.GRAPHEMIC_BREAKDOWN.map((grapheme, i) => (
              <BoxWithBall
                key={`stage0-${i}`}
                filled={i < graphemeIndex}
                grapheme={grapheme}
                disabled={true}
                showGrapheme={true}
              />
            ))}
          </div>
        </>
      );
    }

    return (
      <>
        {IMG_URL && (<ElkoninImage src={IMG_URL} />)}
        <div className="elkonin-boxes">
          {[...graphemes, ...distractors].map((grapheme, i) => (
            <BoxWithBall
              key={`stage1-${i}`}
              filled={i < graphemeIndex}
              grapheme={grapheme}
              showGrapheme={stage >= 2}
              disabled={stage !== 2}
              onClickBall={() => {
                if (i !== graphemeIndex) return;
                onClickBall();
              }}
            />
          ))}
        </div>
      </>
    );
  }

  function onClickBall() {
    const i = graphemeIndex;
    setGraphemeIndex(i + 1);
  }
}

function ElkoninBox3(props) {
  const { locale } = getUserData();
  const { word, wordMetadata, speech, startFakeListen, stopFakeListen } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN, IMG_URL } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");

  const [stage, setStage] = useState(0);
  const [graphemeIndex, setGraphemeIndex] = useState(0);
  const [flashIndex, setFlashIndex] = useState(-1);

  const { speak, speakFile, listenWord } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const [timer, setTimer] = useState(null);

  useEffect(() => {
    return () => clearTimeout(timer);
  }, [timer]);

  useEffect(() => {
    if (stage !== 1) return;
    if (graphemeIndex === graphemes.length) return;
    const job = setTimeout(onClickBall, 7500);
    setTimer(job);
  }, [stage, graphemeIndex]);

  const files = getAudioForPhonemes(PHON, locale);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0: {
          const nSounds = files.length;
          await cancellablePromise(speak(ttsStrings.intro_1({ nSounds })));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        }
        case 1:
          startFakeListen()
          break;
        case 2:
          await cancellablePromise(sleep(2000));
          await cancellablePromise(speak(ttsStrings.ask_word));
          flashBoxes();
          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {IMG_URL && (<ElkoninImage src={IMG_URL} />)}
      {renderContent()}
      <ListeningStatus />
    </Container>
  );

  function renderContent() {
    return (
      <div className="elkonin-boxes">
        {graphemes.map((grapheme, i) => (
          <BoxWithBall
            key={i}
            filled={i < graphemeIndex}
            grapheme={grapheme}
            disabled={stage !== 1}
            flash={i === flashIndex}
            onClickBall={() => {
              if (i !== graphemeIndex) return;
              onClickBall();
            }}
          />
        ))}
      </div>
    );
  }

  function onClickBall() {
    const i = graphemeIndex;
    speakFile(files[i]);
    setGraphemeIndex(i + 1);

    // all boxes have been filled
    if (i === graphemes.length - 1) {
      stopFakeListen();
      setTimer(null);
      setStage(2);
    }
  }

  async function flashBoxes() {
    try {
      for (let i = 0; i < graphemes.length; i++) {
        setFlashIndex(i);
        await cancellablePromise(sleep());
      }
      setFlashIndex(-1);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }
}

function ElkoninBox4(props) {
  const { locale } = getUserData();
  const demo = getDemoWords(locale);
  const { word, wordMetadata, speech, ftue, startFakeListen, stopFakeListen } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN, IMG_URL } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [graphemeIndex, setGraphemeIndex] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const { speak, speakSequence, speakPhonemes, listenWord } = useConversation(
    speech,
    listen
  );
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, locale);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro_2));
          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({
            phon: demo.cat.PHON,
            delay: 750,
            onSpeak: (i) => setGraphemeIndex(i + 1),
          }));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speak(ttsStrings.ignore_extras));
          setStage(1);
          break;
        case 1:
          setGraphemeIndex(0);
          if (ftue) await cancellablePromise(speak(ttsStrings.your_turn));
          await cancellablePromise(speak(ttsStrings.ask_sounds_2));
          setStage(2);
          break;
        case 2:
          startFakeListen();
          break;
        case 3: {
          await cancellablePromise(sleep(2000));
          await cancellablePromise(speak(ttsStrings.ask_blend));
          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          if (status === "match"){
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.correct,
                {textToSpeak: word, skipTranslation: true},
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.correct} ${word}`));
          }
          else {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.struggle_part_1,
                {textToSpeak: word, skipTranslation: true},
                ttsStrings.struggle_part_2,
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.struggle_part_1} ${word}. ${ttsStrings.struggle_part_2}`));
          }
          props.close();
          break;
        }
        case 4: {
          // feedback level 1
          // reset if too many balls
          if (graphemeIndex >= graphemes.length) {
            setGraphemeIndex(0);
            await cancellablePromise(speak(ttsStrings.too_many_sounds));
          }
          const nSounds = files.length;

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.describe_1,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.describe_2({ nSounds }),
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.describe_1} ${word} ${ttsStrings.describe_2({ nSounds })}`));

          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({ phon: PHON, delay: 500 }));

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.feedback_1,
              {textToSpeak: word, skipTranslation: true},
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.feedback_1} ${word}`));

          setAttempts(1);
          setStage(2);
          break;
        }
        case 5:
          // feedback level 2
          setGraphemeIndex(0);

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.feedback_2_part_1,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.feedback_2_part_2,
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.feedback_2_part_1} ${word} ${ttsStrings.feedback_2_part_2}`));

          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({
            phon: PHON,
            delay: 750,
            onSpeak: (i) => setGraphemeIndex(i + 1),
          }));
          setStage(3);
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  // evaluate correctness on graphemeIndex change
  // apply a timeout to allow "too many balls" or "not enough balls" feedback
  useEffect(() => {
    if (stage !== 2) return;

    let timeout = 4500;

    // reduce timeout when correct
    const isCorrect = graphemeIndex === graphemes.length;
    if (isCorrect) timeout = 2500;

    // reduce timeout when maxed out on balls
    if (graphemeIndex === graphemes.length + 1) timeout = 2500;

    const job = setTimeout(() => {
      stopFakeListen();
      if (isCorrect) setStage(3);
      else {
        // incorrect
        if (attempts === 0) setStage(4);
        if (attempts === 1) setStage(5);
      }
    }, timeout);
    return () => clearTimeout(job);
  }, [graphemeIndex, stage]);

  return (
    <Container icon={icon}>
      {renderContent()}
      <div style={{ marginTop: 30 }}>
        <ListeningStatus />
      </div>
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      return (
        <>
          <ElkoninImage src={demo.cat.IMG_URL} />
          <div className="elkonin-boxes">
            {demo.cat.GRAPHEMIC_BREAKDOWN.map((grapheme, i) => (
              <BoxWithBall
                key={`stage0-${i}`}
                filled={i < graphemeIndex}
                grapheme={grapheme}
                disabled={true}
                showGrapheme={true}
              />
            ))}
          </div>
        </>
      );
    }

    return (
      <>
        {IMG_URL && (<ElkoninImage src={IMG_URL} />)}
        <div className="elkonin-boxes">
          {[...graphemes, ""].map((grapheme, i) => (
            <BoxWithBall
              key={`stage1-${i}`}
              filled={i < graphemeIndex}
              grapheme={grapheme}
              showGrapheme={stage >= 2}
              disabled={stage !== 2}
              onClickBall={() => {
                if (i !== graphemeIndex) return;
                onClickBall();
              }}
            />
          ))}
        </div>
      </>
    );
  }

  function onClickBall() {
    const i = graphemeIndex;
    setGraphemeIndex(i + 1);
  }
}

function ElkoninBox99(props) {
  const { locale } = getUserData();
  const { word, wordMetadata, speech, ftue, tutorMode, phrase, currentWordIndex, exampleWord, exampleWordMetadata, startPhrase, paused, resumePhrase, setWillHandlePause } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(ftue ? 0 : 1);
  const [graphemeIndex, setGraphemeIndex] = useState(0);
  const [handlingPause, setHandlingPause] = useState(false);
  const [ballsDisabled, setBallsDisabled] = useState(true);

  useEffect(() => {
    if(stage === 1){
      if (graphemeIndex > graphemes.length - 1) {
        setWillHandlePause(true);
        setHandlingPause(true);
        setBallsDisabled(true);
        setStage(2);
      }
    }
  }, [graphemeIndex]);

  const { speak, speakSequence, speakSlowly, speakPhonemes, listenPhoneme, speakFile, stopListening } =
    useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, locale);

  const graphemeIndexRef = useRef();
  graphemeIndexRef.current = graphemeIndex;

  const { listenForBlendedWordComplete, blendedWordListeningCompleteCallback } = useBlendingTaskPlacementListening({
    currentWordIndex,
    phrase,
    speech,
    enabled: tutorMode,
    onBlendedWordListenStart,
  });

  const onBlendedWordListenStart = ()  =>{}
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    return () => clearTimeout(timer);
  }, [timer]);

  useEffect(() => {
    if (!props.isMountedRef.current) return;

    // Handle the first pause while listening for the word by prompting the student
    if(paused && stage === 2 && handlingPause && props.speech.isListening){
      stopListening();
      setHandlingPause(false);
      speakBlendedWordPrompt();
    }

    // Handle the 2nd pause while listening to the word by essentially giving feedback and ending the phrase
    if(paused && stage === 3 && handlingPause){
      setHandlingPause(false);
      onStruggle()
    }
  },[paused]);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  useEffect(()=>{
    // If the placement manager advanced the word and the pause timer hasn't fired, we're done
    if(currentWordIndex > 0 && handlingPause){
      setStage(4);
    }
  },[currentWordIndex])

  async function speakBlendedWordPrompt() {
    if (!props.isMountedRef.current) return;

    try {
      await cancellablePromise(speak({
        textToSpeak: ttsStrings.the_sounds_are,
      }));

      await cancellablePromise(speakPhonemes({
        phon: PHON,
        delay: 100,
      }));

      await cancellablePromise(speak({
        textToSpeak: ttsStrings.what_word,
      }));
      setStage(3);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onMatch() {
    if (!props.isMountedRef.current) return;

    try {
      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.correct,
          {textToSpeak: word, skipTranslation: true},
        ]));
      }
      else await cancellablePromise(speak(`${ttsStrings.correct} ${word}`));
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onStruggle() {
    if (!props.isMountedRef.current) return;

    try {
      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.struggle_part_1,
          {textToSpeak: word, skipTranslation: true},
          ttsStrings.struggle_part_2,
        ]));
      }
      else await cancellablePromise(speak(`${ttsStrings.struggle_part_1} ${word}. ${ttsStrings.struggle_part_2}`));

      if(props.advanceOneWord){
        props.advanceOneWord();
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function speakIntro() {
    if (!props.isMountedRef.current) return;

    try {
      if(tutorMode){
        if(getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.feedback_1,
            {textToSpeak: word, skipTranslation: true},
          ]));
        } else{
          await cancellablePromise(speak(`${ttsStrings.feedback_1} ${word}`));
        }
      } else {
        await cancellablePromise(speak(ttsStrings.lets_work_on({word})))
        await cancellablePromise(speak(ttsStrings.ask_sounds_1({word})));
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function speakFTUEIntro() {
    if (!props.isMountedRef.current) return;

    try {
      if(tutorMode){
        if(getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.tutor_intro_part_1,
            {textToSpeak: exampleWord, skipTranslation: true},
            ttsStrings.tutor_intro_part_2,
            {textToSpeak: exampleWord, skipTranslation: true},
          ]));
        } else{
          await cancellablePromise(speak(`${ttsStrings.tutor_intro_part_1} ${exampleWord}. ${ttsStrings.tutor_intro_part_2} ${exampleWord}`));
        }
      } else {
        await cancellablePromise(speak(ttsStrings.intro_2));
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function speakFTUEYourTurnPrompt() {
    if (!props.isMountedRef.current) return;

    try {
      if(tutorMode){
        if(getUserData().bilingualMode) {
          await cancellablePromise(speakSequence([
            ttsStrings.your_turn_tutor,
            {textToSpeak: word, skipTranslation: true},
          ]));
        } else{
          await cancellablePromise(speak(`${ttsStrings.your_turn_tutor} ${word}`));
        }
      } else {
        await cancellablePromise(speak(ttsStrings.your_turn));
        await cancellablePromise(speak(ttsStrings.lets_work_on({word})))
        await cancellablePromise(speak(ttsStrings.ask_sounds_1({word})));
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onNoActivity(){
    if (!props.isMountedRef.current) return;

    try {
      //do "ask sound one"
      await cancellablePromise(speak(ttsStrings.drag_the_circles));
      //TODO: insert the second thing here - "Say the sounds, etc"
      const moveTimeout = setTimeout(onClickBall, MAX_LISTEN_TIME);
      setTimer(moveTimeout);
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  function onClickBall() {
    clearTimeout(timer);
    const i = graphemeIndexRef.current;
    speakFile(files[i]);
    const newIndex = i + 1;
    setGraphemeIndex(newIndex);

    if (newIndex <= graphemes.length - 1) {
      const moveTimeout = setTimeout(onClickBall, MAX_LISTEN_TIME);
      setTimer(moveTimeout);
    }
  }

  function onInterventionModeTimeout(){
    if (!props.isMountedRef.current) return;

    if(blendedWordListeningCompleteCallback){
      blendedWordListeningCompleteCallback(false);
    }
  }

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        // STAGE 0: Amira is giving her intro and modeling
        case 0:
          stopListening();
          await cancellablePromise(speakFTUEIntro());
          await cancellablePromise(sleep());
          await cancellablePromise(speakPhonemes({
            phon: exampleWordMetadata ? exampleWordMetadata.PHON : PHON,
            delay: 750,
            onSpeak: (i) => setGraphemeIndex(i + 1),
          }));
          await cancellablePromise(speak(ttsStrings.blend));
          await cancellablePromise(speakSlowly({textToSpeak: exampleWord || word, skipTranslation: getUserData().bilingualMode}));
          await cancellablePromise(sleep());
          setGraphemeIndex(0);
          await cancellablePromise(speakFTUEYourTurnPrompt());
          setStage(1);
          break;

        //STAGE 1: Amira gives the intro and the student is moving balls;
        case 1:
          if (!ftue) await cancellablePromise(speakIntro());

          setBallsDisabled(false);

          const noActivityTimeout = setTimeout(onNoActivity, MAX_LISTEN_TIME);
          setTimer(noActivityTimeout);

          if(!tutorMode){
            for (let i = 0; i < graphemes.length; i++) {
              const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] }));
              if (status === "skip") return props.close && props.close();
            }
          }

          break;

        // STAGE 2: LISTEN FOR STUDENT TO SAY THE BLENDED WORD UNTIL A TIMEOUT
        case 2:
          await cancellablePromise(sleep(2000));
          await cancellablePromise(speak(ttsStrings.ask_blend));
          if(!tutorMode){
            // if we're in tutor mode, whether phoneme placement is enabled or not, use the word index coming through from the placement manager
            const job = setTimeout(onInterventionModeTimeout, 7500);
            setTimer(job);
            const success = await cancellablePromise(listenForBlendedWordComplete({speech: props.speech})); //The reason to do it this way instead of in a more "reacty" way is to keep the code structure the same for other uses (not tutor) of this component
            if (success) {
              await cancellablePromise(onMatch());
            } else {
              await cancellablePromise(onStruggle());
            }
            props.close();
          } else {
            startPhrase();
            break;
          }
          break;

        // In this stage, we're just letting ReaderManager do its thing until the end of the phrase
        // NOTE: This is where we can add patter
        case 3:
          setHandlingPause(true);
          props.speech.startListening();
          resumePhrase();
          break;

        //In this stage, placement detected the word
        case 4:
          await cancellablePromise(onMatch());
          props.close();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      {!tutorMode && <ListeningStatus />}
    </Container>
  );

  function renderContent() {
    return (
      <div className="elkonin-boxes r-3">
        {graphemes.map((grapheme, i) => {
          const filled = i < graphemeIndex;

          let showGrapheme = false;
          if (stage === 1 && filled) showGrapheme = true;
          if (stage > 1) showGrapheme = true;

          return (
            <BoxWithBall
              key={i}
              filled={filled}
              grapheme={grapheme}
              showGrapheme={showGrapheme}
              disabled={ballsDisabled}
              graphemePosition="above"
              onClickBall={() => {
                if (i !== graphemeIndex) return;
                onClickBall();
              }}
            />
          );
        })}
      </div>
    );
  }
}

function BoxWithBall(props) {
  const { filled, grapheme, showGrapheme, disabled, graphemePosition, flash } =
    props;

  let offsetY = 0;
  if (!filled) offsetY = 110;

  const ballStyle = { transform: `translateY(${offsetY}%)` };
  if (props.onClickBall && !disabled) ballStyle.cursor = "pointer";

  const boxClass = classNames({
    "elkonin-box-above": graphemePosition === "above",
    "elkonin-box-flash": flash,
    "elkonin-hide-grapheme": !showGrapheme,
  });

  return (
    <React.Fragment>
      <div className="box-wrapper">
        <Box className={boxClass}>
          <div className="elkonin-grapheme">
            <GraphemeText grapheme={grapheme} />
          </div>
          <div
            className="elkonin-ball"
            style={ballStyle}
            {...doNotUseAccessibleStaticElementInteractions({onTouchStart: onClickBall, onMouseDown: onClickBall})}
          />
        </Box>
      </div>
    </React.Fragment>
  );

  function onClickBall() {
    if (filled || disabled) return;
    if (props.onClickBall) props.onClickBall();
  }
}

function ElkoninImage(props) {
  return <Image src={props.src} className="elkonin-image" />;
}
